import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import StatusWall from "@/pages/StatusWall";
import Dashboard from "@/pages/Dashboard";
import Chapters from "@/pages/Chapters";
import ChapterReader from "@/pages/ChapterReader";
import Quiz from "@/pages/Quiz";
import FinalExam from "@/pages/FinalExam";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import { Impressum, Datenschutz } from "@/pages/Legal";

function Loading() {
  return <div className="min-h-screen flex items-center justify-center bg-[#0d0d0d]">
    <div className="text-[#D21F2A] font-serif text-xl animate-pulse">4Flair …</div>
  </div>;
}

// Zugang: angemeldet UND freigegeben
function CourseRoute({ children }) {
  const { user, accessStatus, loading } = useApp();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  if (accessStatus !== "approved") return <Navigate to="/status" replace />;
  return children;
}

function AuthedRoute({ children }) {
  const { user, loading } = useApp();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useApp();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/admin-login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { t } = useApp();
  useEffect(() => {
    const p = location.pathname;
    const suffix = " | 4Flair Bar Academy";
    let title = t("meta_base_title");
    if (p === "/") title = t("meta_base_title");
    else if (p.startsWith("/login")) title = t("pt_login") + suffix;
    else if (p.startsWith("/reset-password")) title = t("pt_reset") + suffix;
    else if (p.startsWith("/admin-login")) title = t("pt_adminlogin") + suffix;
    else if (p.startsWith("/admin")) title = t("pt_admin") + suffix;
    else if (p.startsWith("/dashboard")) title = t("pt_dashboard") + suffix;
    else if (p.endsWith("/quiz")) title = t("pt_quiz") + suffix;
    else if (p.startsWith("/chapters/")) title = t("pt_chapter") + suffix;
    else if (p.startsWith("/chapters")) title = t("pt_chapters") + suffix;
    else if (p.startsWith("/final")) title = t("pt_final") + suffix;
    else if (p.startsWith("/profile")) title = t("pt_profile") + suffix;
    else if (p.startsWith("/status")) title = t("pt_status") + suffix;
    else if (p.startsWith("/impressum")) title = t("pt_impressum") + suffix;
    else if (p.startsWith("/datenschutz")) title = t("pt_datenschutz") + suffix;
    document.title = title;
  }, [location.pathname, t]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/profile" element={<AuthedRoute><Profile /></AuthedRoute>} />
      <Route path="/status" element={<AuthedRoute><StatusWall /></AuthedRoute>} />
      <Route path="/dashboard" element={<CourseRoute><Dashboard /></CourseRoute>} />
      <Route path="/chapters" element={<CourseRoute><Chapters /></CourseRoute>} />
      <Route path="/chapters/:chapterId" element={<CourseRoute><ChapterReader /></CourseRoute>} />
      <Route path="/chapters/:chapterId/quiz" element={<CourseRoute><Quiz /></CourseRoute>} />
      <Route path="/final" element={<CourseRoute><FinalExam /></CourseRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" theme="dark" />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
